/*
 * Copyright (c) 2025 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React from "react";
import {Box, List, ListItem, Typography} from "@mui/material";
import ArgosFlexbox from "../../../../../../../components/Containers/ArgosFlexbox";

interface Props {
  idsData: any;
  objectIdToName: Record<string, string>;
  greenlineIdToProperties: Record<string, { name: string }>;
}

const propertyLabels: Record<string, string> = {
  no_height: "height",
  no_width: "width",
  no_rcs: "RCS",
};

const InvalidObjectsDisplay: React.FC<Props> = ({idsData, objectIdToName, greenlineIdToProperties}) => {
  if (!idsData) {
    return (
      <Typography variant="h6">
        No data available to check for invalid objects.
      </Typography>
    );
  } else if (Object.keys(idsData).length === 0) {
    return (
      <Typography variant="h6">
        All objects are valid for blue line generation. Click GENERATE to generate blue lines.
      </Typography>
    );
  }

  return (
    <ArgosFlexbox vertical style={{overflow: 'auto', flex: 1}}>
      <Typography variant="h6" gutterBottom>
        There are invalid objects. Blue line generation will skip them:
      </Typography>
      <div style={{flex: 1, overflow: 'auto'}}>
        {Object.entries(idsData).map(([greenlineId, missing]: any) => {
          const greenlineName = greenlineIdToProperties?.[greenlineId]?.name ?? greenlineId;

          // Build a map: objectId -> array of missing property labels
          const objectToMissingProps: Record<string, string[]> = {};
          Object.entries(propertyLabels).forEach(([propKey, label]) => {
            const objects = Object.keys(missing[propKey] || {});
            objects.forEach((objId) => {
              if (!objectToMissingProps[objId]) objectToMissingProps[objId] = [];
              objectToMissingProps[objId].push(label);
            });
          });

          if (Object.keys(objectToMissingProps).length === 0) return null;

          return (
            <Box key={greenlineId} mb={2}>
              <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                {greenlineName}
              </Typography>
              <List dense>
                {Object.entries(objectToMissingProps).map(([objId, missingProps]) => (
                  <ListItem key={objId} sx={{pl: 2}}>
                    {objectIdToName[objId] ?? objId} &mdash; Missing: {missingProps.join(", ")}
                  </ListItem>
                ))}
              </List>
              {missing.allObjectsInvalid && (
                <Typography variant="body2" color="error" sx={{pl: 2, mt: 1}}>
                  All selected objects for this sensor are invalid. Cannot generate blue line.
                </Typography>
              )}
            </Box>
          );
        })}
      </div>
    </ArgosFlexbox>
  );
};

export default InvalidObjectsDisplay;